import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Typography } from '@material-ui/core';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { FormField } from 'common/components/FormField';
import { TextField } from 'common/components/TextField';
import { Button } from 'common/components/buttons/Button';
import { IUserSignupArgsType } from 'graphql/types';
import { validators } from 'common/constants';

type TInitialValues = {
    password: string;
    passwordConfirm: string;
    termsAgreed: boolean;
};

interface IProps {
    className?: string;
    loading?: boolean;
    onSubmit(values: IUserSignupArgsType): void;
}

const BaseSetPasswordForm = ({ className, loading, onSubmit }: IProps) => {
    const { t } = useTranslation();
    const formik = useFormik({
        initialValues: {
            password: '',
            passwordConfirm: '',
            termsAgreed: false,
        },
        validationSchema: Yup.object().shape({
            password: validators.password,
            passwordConfirm: Yup.string()
                .required(t('validators.passwordConfirm.required'))
                .oneOf(
                    [Yup.ref('password')],
                    t('validators.passwordConfirm.invalid')
                ),
        }),
        onSubmit: (values, { resetForm }) => {
            resetForm();

            onSubmit(values);
        },
    });

    const {
        values,
        errors: formikErrors,
        touched,
        handleChange,
        handleSubmit,
    } = formik;

    const getFieldError = (fieldName: keyof TInitialValues) => {
        if (touched[fieldName] && formikErrors[fieldName]) {
            return formikErrors[fieldName];
        }

        return '';
    };

    return (
        <form className={className}>
            <Typography color="primary" variant="h2">
                {t('account.resetPassword.title')}
            </Typography>

            <Box className="description">
                <Typography color="textSecondary" variant="body2">
                    {t('account.resetPassword.instructions')}
                </Typography>
            </Box>

            <FormField>
                <TextField
                    error={getFieldError('password')}
                    id="password"
                    label={t('formfields.password')}
                    type="password"
                    value={values.password}
                    variant="filled"
                    onChange={handleChange}
                />
            </FormField>
            <FormField>
                <TextField
                    error={getFieldError('passwordConfirm')}
                    id="passwordConfirm"
                    label={t('formfields.passwordConfirm')}
                    type="password"
                    value={values.passwordConfirm}
                    variant="filled"
                    onChange={handleChange}
                />
            </FormField>

            <Box mt={6}>
                <Button
                    fullWidth
                    loading={loading}
                    onClick={() => handleSubmit()}
                >
                    {t('account.resetPassword.submit')}
                </Button>
            </Box>
        </form>
    );
};

export const SetPasswordForm = styled(BaseSetPasswordForm)`
    width: 100%;

    .description {
        margin-bottom: ${({ theme }) => `${theme.spacing(10)}px`};
    }
`;
